import AddElemPlus from "@builder/components/app/OliverBuildFrame/AddElemPlus";
import ImagePlacholder from "@builder/components/app/OliverBuildFrame/ImagePlaceholder";
import {
    cartPaymentData,
    CLSampleData,
    customSaleAddToCartPreviewPrice,
    customSaleTaxExample,
    EOSSamplev2,
    previewCustomerNotes,
    radioOptions,
    recieptSampleData,
    searchOptions,
    simpleCartData,
    variationData
} from "@builder/components/app/OliverBuildFrame/props";
import DynamicWrapper, { SingleStoreDataWrapper } from "@builder/components/wrappers/DynamicWrapper";
import { formatAmount, formatCurrency } from "@builder/utils/format";
import { BuildComponentKey, ImageNode, SearchNode } from "@common/build/types";
import BuilderBarcode from "@common/components/BuilderBarcode";
import BuilderButton from "@common/components/BuilderButton";
import BuilderCashLedger from "@common/components/BuilderCashLedger";
import BuilderCFD from "@common/components/BuilderCFD";
import BuilderCustomNumpad from "@common/components/BuilderCustomSale/BuilderNumpad/BuilderCustomNumpad";
import BuilderDiv from "@common/components/BuilderDiv";
import BuilderDropdownField from "@common/components/BuilderDropdownField";
import BuilderDynamicGrid from "@common/components/BuilderDynamicGrid";
import BuilderEmbedSection from "@common/components/BuilderEmbedSection";
import BuilderEOS from "@common/components/BuilderEOS";
import BuilderGlobalBlockPrint from "@common/components/BuilderGlobalBlockPrint";
import BuilderIcon from "@common/components/BuilderIcon";
import BuilderIframe from "@common/components/BuilderIframe";
import BuilderImage from "@common/components/BuilderImage";
import { BuilderComponentProps } from "@common/components/BuilderInterface";
import BuilderMenu from "@common/components/BuilderMenu";
import BuilderPage from "@common/components/BuilderPage";
import BuilderPopup from "@common/components/BuilderPopup";
import BuilderProductQuantity from "@common/components/BuilderProductQuantity";
import BuilderProductVariations from "@common/components/BuilderProductVariations";
import BuilderRadioField from "@common/components/BuilderRadioField";
import BuilderReceipt from "@common/components/BuilderReceipt";
import BuilderSearch from "@common/components/BuilderSearch";
import BuilderSearchField from "@common/components/BuilderSearchField";
import BuilderSimpleCart from "@common/components/BuilderSimpleCart";
import BuilderSimpleCartUnfixed from "@common/components/BuilderSimpleCartUnfixed";
import BuilderSlideout from "@common/components/BuilderSlideout";
import BuilderText from "@common/components/BuilderText";
import HookWrapper from "@common/components/HookWrapper";
import { ReactNode } from "react";
import IframePlaceholder from "./IframePlaceholder";
import BuilderOrderData from "@common/components/BuilderOrderData";
import BuilderOrderRefunds from "@common/components/BuilderOrderRefunds";
import EmptyWrapper from "@builder/components/wrappers/EmptyWrapper";
import BuilderPaymentGrid from "@common/components/BuilderPaymentGrid";
import { ActiveEntityType } from "@common/build/activeEntityType";
import { DynamicDataSectionProvider } from "@common/Contexts/DynamicDataTypeContext";
import BuilderCustomerNotes from "@common/components/BuilderCustomerNotes";
import BuilderVismaElement from "@common/components/BuilderVismaElements/BuilderVismaElement";
import BuilderVismaPage from "@common/components/BuilderVismaElements/BuilderVismaPage";
import BuilderVismaButton from "@common/components/BuilderVismaElements/BuilderVismaButton";
import BuilderVismaInput from "@common/components/BuilderVismaElements/BuilderVismaInput";
import BuilderVismaButtonList from "@common/components/BuilderVismaElements/BuilderVismaButtonList";
import BuilderLinkedInputDisplay from "@common/components/BuilderCustomSale/BuilderNumpad/BuilderLinkedInputDisplay";
import BuilderEditableLabel from "@common/components/BuilderCustomSale/BuilderEditableLabel";
import BuilderTaxConfiguration from "@common/components/BuilderCustomSale/BuilderTaxConfiguration";
import BuilderCustomSale from "@common/components/BuilderCustomSale";
import BuilderDivider from "@common/components/BuilderDivider";
import PaymentGridWrapper from "@builder/components/wrappers/PaymentGridWrapper";
import SmartGridWrapper from "@builder/components/wrappers/SmartGridWrapper";
import SmartTileWrapper from "@builder/components/wrappers/SmartTileWrapper";

export const componentWrapperMap: { [key in BuildComponentKey]: (props: BuilderComponentProps, key?: string) => ReactNode } = {
    button: props => <HookWrapper component={BuilderButton} {...props} />,
    text: props => <HookWrapper component={BuilderText} {...props} formatCurrency={formatCurrency} formatAmount={formatAmount} />,
    barcode: props => <HookWrapper component={BuilderBarcode} {...props} />,
    menu: props => <HookWrapper component={BuilderMenu} {...props} />,
    icon: props => <HookWrapper component={BuilderIcon} {...props} />,
    simpleCart: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.CART,
                dynamicDataObject: props?.storeData
            }}
        >
            <EmptyWrapper
                component={BuilderSimpleCart}
                {...props}
                activeCartData={simpleCartData}
                payments={cartPaymentData}
                formatCurrency={formatCurrency}
                formatAmount={formatAmount}
            />
        </DynamicDataSectionProvider>
    ),
    unfixedCart: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.CART,
                dynamicDataObject: props?.storeData
            }}
        >
            <EmptyWrapper
                component={BuilderSimpleCartUnfixed}
                {...props}
                activeCartData={simpleCartData}
                payments={cartPaymentData}
                formatCurrency={formatCurrency}
                formatAmount={formatAmount}
            />
        </DynamicDataSectionProvider>
    ),
    cartSection: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.CART,
                dynamicDataObject: props?.storeData
            }}
        >
            <HookWrapper
                component={BuilderDiv}
                {...props}
                builderProps={{
                    ...props.builderProps,
                    extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                    isContainer: !props.builderProps?.containerDisabled
                }}
            />
        </DynamicDataSectionProvider>
    ),
    search: props => {
        const optionTypes: string[] = [];
        const sNode = props.node as SearchNode;
        if (sNode.included?.customers) optionTypes.push("customer");
        if (sNode.included?.orders) optionTypes.push("order");
        if (sNode.included?.products) optionTypes.push("product");
        const filteredSearchOptions = searchOptions.filter(option => optionTypes.includes(option.type));

        return (
            <HookWrapper
                component={BuilderSearch}
                {...props}
                value={props.builderProps?.active ? "Example search" : ""}
                options={props.builderProps?.active ? filteredSearchOptions : undefined}
            />
        );
    },
    div: props => (
        <HookWrapper
            component={BuilderDiv}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    repeaterDiv: props => (
        <HookWrapper
            component={BuilderDiv}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    productSection: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.PRODUCT,
                dynamicDataObject: props?.storeData
            }}
        >
            <HookWrapper
                component={BuilderDiv}
                {...props}
                builderProps={{
                    ...props.builderProps,
                    extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                    isContainer: !props.builderProps?.containerDisabled
                }}
            />
        </DynamicDataSectionProvider>
    ),
    orderSection: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.ORDER,
                dynamicDataObject: props?.storeData
            }}
        >
            <HookWrapper
                component={BuilderDiv}
                {...props}
                builderProps={{
                    ...props.builderProps,
                    extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                    isContainer: !props.builderProps?.containerDisabled
                }}
            />
        </DynamicDataSectionProvider>
    ),
    customerSection: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.CUSTOMER,
                dynamicDataObject: props?.storeData
            }}
        >
            <HookWrapper
                component={BuilderDiv}
                {...props}
                builderProps={{
                    ...props.builderProps,
                    extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                    isContainer: !props.builderProps?.containerDisabled
                }}
            />
        </DynamicDataSectionProvider>
    ),
    customerNotes: props => (
        <EmptyWrapper
            component={BuilderCustomerNotes}
            {...props}
            builderProps={{
                ...props.builderProps,
                previewNotes: previewCustomerNotes
            }}
        />
    ),

    templateDiv: props => (
        <HookWrapper
            component={BuilderDiv}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    baseLayerPage: props => (
        <HookWrapper
            component={BuilderPage}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined
            }}
        />
    ),
    baseLayerPopup: props => (
        <HookWrapper
            component={BuilderPopup}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined
            }}
        />
    ),
    baseLayerCFD: props => (
        <HookWrapper
            component={BuilderCFD}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined
            }}
        />
    ),
    globalBlockPrint: props => (
        <DynamicDataSectionProvider
            value={{
                dynamicDataScope: ActiveEntityType.ORDER,
                dynamicDataObject: props?.storeData
            }}
        >
            <HookWrapper component={BuilderGlobalBlockPrint} {...props} />
        </DynamicDataSectionProvider>
    ),
    globalBlockCFD: props => (
        <HookWrapper
            component={BuilderDiv}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: Array.isArray(props?.children) && props.children.length > 0 ? <AddElemPlus /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    image: props => (
        <HookWrapper
            component={BuilderImage}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !(props.node as ImageNode)?.image?.url && !props.node.dynamicData ? <ImagePlacholder /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    cashButton: props => <HookWrapper component={BuilderButton} {...props} />,
    printButton: props => <HookWrapper component={BuilderButton} {...props} />,
    receiptData: props => (
        <HookWrapper
            component={BuilderReceipt}
            {...props}
            formatCurrency={formatCurrency}
            formatAmount={formatAmount}
            receiptData={recieptSampleData}
        />
    ),
    orderData: props => (
        <SingleStoreDataWrapper
            component={BuilderOrderData}
            singleStoreDataType="order"
            {...props}
            formatCurrency={formatCurrency}
            formatAmount={formatAmount}
        />
    ),
    orderRefunds: props => (
        <SingleStoreDataWrapper
            component={BuilderOrderRefunds}
            singleStoreDataType="order"
            {...props}
            formatCurrency={formatCurrency}
            formatAmount={formatAmount}
        />
    ),
    cashLedgerData: props => <HookWrapper component={BuilderCashLedger} {...props} cashLedgerData={CLSampleData} formatCurrency={formatCurrency} />,
    eosData: props => <HookWrapper component={BuilderEOS} {...props} eosData={EOSSamplev2} formatCurrency={formatCurrency} />,
    productQuantity: props => <HookWrapper component={BuilderProductQuantity} {...props} />,
    productVariations: props => (
        <EmptyWrapper
            component={BuilderProductVariations}
            {...props}
            dataObject={variationData}
            variationData={variationData}
            formatCurrency={formatCurrency}
            formatAmount={formatAmount}
            readOnly
        />
    ),
    customNumpad: props => <HookWrapper component={BuilderCustomNumpad} {...props} />,
    linkedInputDisplay: props => (
        <HookWrapper
            component={BuilderLinkedInputDisplay}
            {...props}
            formatCurrency={formatCurrency}
            formatAmount={formatAmount}
            price={100} // preview value
            quantity={2} // preview value
        />
    ),
    tapToPay: props => <HookWrapper component={BuilderButton} {...props} />,
    terminalButton: props => <HookWrapper component={BuilderButton} {...props} />,
    motoButton: props => <HookWrapper component={BuilderButton} {...props} />,
    customPayment: props => <HookWrapper component={BuilderButton} {...props} />,
    vendaraPayment: props => <HookWrapper component={BuilderButton} {...props} />,
    homeMenuLink: props => <HookWrapper component={BuilderButton} {...props} />,
    productGrid: (props: BuilderComponentProps) => (
        <DynamicWrapper
            component={BuilderDynamicGrid}
            {...props}
            builderProps={{
                ...props.builderProps,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    productList: (props: BuilderComponentProps) => (
        <DynamicWrapper
            component={BuilderDynamicGrid}
            {...props}
            builderProps={{
                ...props.builderProps,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    orderList: (props: BuilderComponentProps) => (
        <DynamicWrapper
            component={BuilderDynamicGrid}
            {...props}
            builderProps={{
                ...props.builderProps,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    customerList: (props: BuilderComponentProps) => (
        <DynamicWrapper
            component={BuilderDynamicGrid}
            {...props}
            builderProps={{
                ...props.builderProps,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    searchField: props => <HookWrapper component={BuilderSearchField} value={props.builderProps?.active ? "Example search" : ""} {...props} />,
    dropdownField: props => <HookWrapper component={BuilderDropdownField} {...props} builder />,
    radioField: props => <HookWrapper component={BuilderRadioField} radioOptions={radioOptions} selectedOption="all" {...props} />,
    slideout: props => (
        <HookWrapper
            component={BuilderSlideout}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !Array.isArray(props?.children) || props.children.length === 0 ? <AddElemPlus /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
            open={props.builderProps?.active}
        />
    ),
    embedSection: props => (
        <HookWrapper
            component={BuilderEmbedSection}
            {...props}
            builderProps={{
                ...props.builderProps
            }}
        />
    ),
    iframe: props => (
        <HookWrapper
            component={BuilderIframe}
            {...props}
            builderProps={{
                ...props.builderProps,
                extraChild: !(props.node as ImageNode)?.image?.url && !props.node.dynamicData ? <IframePlaceholder /> : undefined,
                isContainer: !props.builderProps?.containerDisabled
            }}
        />
    ),
    paymentGrid: props => <PaymentGridWrapper {...props} component={BuilderPaymentGrid} />,
    vismaElement: props => <HookWrapper {...props} component={BuilderVismaElement} />,
    vismaPage: props => <HookWrapper {...props} component={BuilderVismaPage} />,
    vismaButton: props => <HookWrapper {...props} component={BuilderVismaButton} />,
    vismaInput: props => <HookWrapper {...props} component={BuilderVismaInput} />,
    vismaButtonList: props => <HookWrapper {...props} component={BuilderVismaButtonList} />,
    editableLabel: props => <HookWrapper {...props} component={BuilderEditableLabel} />,
    customSaleAdjustQuantity: props => <HookWrapper component={BuilderProductQuantity} {...props} />,
    customSaleTaxConfiguration: props => <HookWrapper component={BuilderTaxConfiguration} {...props} dropdownOptions={customSaleTaxExample} />,
    customSaleAddToCartButton: props => (
        <HookWrapper
            component={BuilderButton}
            {...props}
            node={{
                ...props.node,
                append: {
                    value: customSaleAddToCartPreviewPrice
                }
            }}
        />
    ),
    customSale: props => <HookWrapper component={BuilderCustomSale} {...props} />,
    divider: props => <HookWrapper component={BuilderDivider} {...props} />,
    smartGrid: props => <SmartGridWrapper {...props} saveLayout={{ editMode: props.builderProps?.active || false }} />,
    smartTile: props => <SmartTileWrapper {...props} />
};
